// Centralized feature flags. Default OFF — set the env var to "true" to enable.
//
// Conventions:
//   - Pre-launch flags that gate entire surfaces ship default OFF here.
//   - Each flag must be flippable per Vercel deployment via env without
//     code change.
//   - When a flag is permanently flipped ON, delete the flag and the
//     gate (don't leave dead branches).

export const BABY_MODULE_ENABLED =
  import.meta.env.VITE_BABY_MODULE_ENABLED === "true";

// CHALLENGE_ENABLED — gates the "Desafio Ativo" home card + related challenge
// surfaces. Off for 15/06 launch. Reactivate for v1.1 (Jul-Ago 2026) after
// challenge-creation flow, completion logic and reward UI are designed.
// The challenge component file (src/pages/MirrorChallenge.tsx) and the
// useChallenges hook are intentionally preserved in repo so flipping this
// flag back to true restores the surface without code archaeology.
export const CHALLENGE_ENABLED =
  (import.meta.env.VITE_CHALLENGE_ENABLED ?? "false") === "true";

// MIRROR_AGENDA_CARDS_ENABLED — kill switch for PR-2 Option B (Mirror chat
// agenda creation via agenda-extract + CreateEventModal). Default ON.
// Backend (mirror-chat edge fn) reads its own MIRROR_AGENDA_CARDS_ENABLED
// from Deno.env to gate the propose_agenda_event tool offering. Frontend
// flag is defense-in-depth: even if a stale Sonnet response somehow emits
// the tool when the kill switch is on, the frontend silently drops it.
export const MIRROR_AGENDA_CARDS_ENABLED =
  (import.meta.env.VITE_MIRROR_AGENDA_CARDS_ENABLED ?? "true").toLowerCase() !== "false";

// MIRROR_CONTEXT_SMART — REMOVIDO 03/08/2026.
// A edge function `mirror-context-smart` foi apagada em 02/08. O flag continuou
// aqui calculando um bucket de rollout para um destino que não existe mais, e o
// `MirrorChat` seguia mandando `useSmartContext` no corpo de TODO turno — campo
// que o `mirror-chat` nunca leu em lugar nenhum. Rollout estava em 0, então o
// valor era sempre false: nada mudou de comportamento ao remover.

// PLANS_V32_LOCK_ENABLED — kill-switch de ops do maquinário de lock visual
// de módulo (blur + Lock + PaywallSheet). Desde 22/08/2026 ele é VÁCUO na
// prática: NENHUM módulo é pago — Brain / Fitness / Nutrition foram
// liberados no Free (decisão de produto) e PHASE1_LOCKED_MODULES está vazio
// em Modules.tsx, então ON/OFF não muda nada hoje. (A versão antiga deste
// comentário dizia que o flag "enforces the module-lock on Brain/Fitness/
// Nutrition for Free users" — deixou de ser verdade quando o Set esvaziou.)
// Segue env-driven (default ON) para o cenário em que um id volte ao Set:
// aí VITE_PLANS_V32_LOCK_ENABLED=false desliga o cadeado sem redeploy.
//
// All paywall plumbing (PaywallSheet component, ModuleCard `locked`
// prop, per-module i18n bundles, sheet state in Modules.tsx) stays in
// place either way; only the boolean that decides lockability is gated.
export const PLANS_V32_LOCK_ENABLED =
  (import.meta.env.VITE_PLANS_V32_LOCK_ENABLED ?? "true").toLowerCase() !== "false";

// SHOW_ONBOARDING_INTRO — gates the pre-login cinematic intro (SplashHero, the
// founder/avatar hero video at /auth). The public landing (aivyv.com) already
// plays the narrative video BEFORE login, so the in-app intro is a duplicated
// step that costs signups. Default OFF: /auth opens straight on the login form,
// and the "Watch intro" replay button is hidden (no orphan path to the video).
// Set VITE_SHOW_ONBOARDING_INTRO=true to restore the old behavior without a code
// change. The SplashHero component + the /splash/*.mp4 assets stay in repo so the
// flag fully round-trips.
export const SHOW_ONBOARDING_INTRO =
  (import.meta.env.VITE_SHOW_ONBOARDING_INTRO ?? "false").toLowerCase() === "true";

// Reviewed personal reminder cards; durable recovery remains available when OFF.
export const MIRROR_REMINDER_CARDS_ENABLED =
  (import.meta.env.VITE_REMINDER_EXECUTOR_ENABLED ?? "true").toLowerCase() !== "false";

// Reviewed Focus actions; durable recovery remains available when OFF.
export const MIRROR_FOCUS_CARDS_ENABLED =
  (import.meta.env.VITE_FOCUS_EXECUTOR_ENABLED ?? "true").toLowerCase() !== "false";

// Reviewed Check-in writes; original owned receipts remain recoverable when OFF.
export const MIRROR_CHECKIN_CARDS_ENABLED =
  (import.meta.env.VITE_CHECKIN_EXECUTOR_ENABLED ?? "true").toLowerCase() !== "false";

// Reviewed Sleep writes; original owned receipts remain recoverable when OFF.
export const MIRROR_SLEEP_CARDS_ENABLED =
  (import.meta.env.VITE_SLEEP_EXECUTOR_ENABLED ?? "true").toLowerCase() !== "false";

// Reviewed body/health writes; recovery remains available when OFF.
export const MIRROR_BODY_HEALTH_CARDS_ENABLED =
  (import.meta.env.VITE_BODY_HEALTH_EXECUTOR_ENABLED ?? "true").toLowerCase() !== "false";

// Reviewed Fitness writes; receipt recovery remains available when OFF.
export const MIRROR_FITNESS_CARDS_ENABLED =
  (import.meta.env.VITE_FITNESS_EXECUTOR_ENABLED ?? "true").toLowerCase() !== "false";

// Reviewed existing-meal changes. Recovery only consults the original receipt when OFF.
export const MIRROR_NUTRITION_MEAL_CARDS_ENABLED =
  (import.meta.env.VITE_NUTRITION_MEAL_EXECUTOR_ENABLED ?? "true").toLowerCase() !== "false";

// Declared cup counts require an explicit review; OFF still permits exact receipt recovery.
export const MIRROR_NUTRITION_HYDRATION_CARDS_ENABLED =
  (import.meta.env.VITE_NUTRITION_HYDRATION_EXECUTOR_ENABLED ?? "true").toLowerCase() !== "false";

// Reviewed nutrition goals; original receipts remain recoverable when OFF.
export const MIRROR_NUTRITION_GOALS_CARDS_ENABLED =
  (import.meta.env.VITE_NUTRITION_GOALS_EXECUTOR_ENABLED ?? "true").toLowerCase() !== "false";

// Curated plan creation requires explicit full review; OFF keeps receipt recovery.
export const MIRROR_FITNESS_CURATED_PLAN_CARDS_ENABLED =
  (import.meta.env.VITE_FITNESS_CURATED_PLAN_EXECUTOR_ENABLED ?? "true").toLowerCase() !== "false";

// Reviewed language configuration/vocabulary; original receipts remain recoverable when OFF.
export const MIRROR_LANGUAGE_ACTIONS_ENABLED =
  (import.meta.env.VITE_LANGUAGE_EXECUTOR_ENABLED ?? "true").toLowerCase() !== "false";

// Reviewed class scheduling; receipt recovery remains available when OFF.
export const MIRROR_LANGUAGE_CLASS_ACTIONS_ENABLED =
  (import.meta.env.VITE_LANGUAGE_CLASS_EXECUTOR_ENABLED ?? "true").toLowerCase() !== "false";

// Explicit Mirror handoff; tutor access still uses the existing profile/Premium gates.
export const MIRROR_LANGUAGE_TUTOR_HANDOFF_ENABLED =
  (import.meta.env.VITE_LANGUAGE_TUTOR_HANDOFF_ENABLED ?? "true").toLowerCase() !== "false";
